import { IComment, Icons, ITagStatus, IToolbar } from '@tecalisdev/infinity';

import { endOfLocalToday, formatDate } from '../../core/utils/utils';
import { IProcessSentMessage, ProcessSentMessageChannel } from '../models/processes.models';

/** Mensaje unificado con ordinal de envío (1-based) para badge `1º`. */
export interface IProcessSentMessageView extends IProcessSentMessage {
	channel: ProcessSentMessageChannel;
	order: number;
}

export interface ISentMessagesFilterState {
	search: string;
	start_at?: string | Date | null;
	end_at?: string | Date | null;
}

/**
 * Fila modal: `tec-comment` + overlay de estado (Figma `11532:51114`).
 * Teléfono vía `tagImage.title` (sin `user` → sin `tec-avatar`).
 * Ordinal en `moreInfo.to` tras el medio. Tag de estado fuera (IComment thread no lo expone).
 */
export interface ISentMessageRowView {
	id: string;
	ordinalLabel: string;
	/** false = histórico / sin body → empty-state «Contenido no disponible». */
	hasContent: boolean;
	/** Tag de entrega junto al teléfono; undefined si la API no manda estado. */
	statusTag?: { label: string; status: ITagStatus };
	comment: IComment;
}

/** Estados API (`communication_log.status`). Alias ES innecesarios: el back solo manda EN. Iconos según Figma. */
const STATUS_TAG_MAP: Record<string, { i18nKey: string; status: ITagStatus; icon: Icons }> = {
	delivered: { i18nKey: 'PROCESSES.DETAIL.MESSAGES.STATUS.DELIVERED', status: 'success', icon: 'icon-check-circle' },
	sent: { i18nKey: 'PROCESSES.DETAIL.MESSAGES.STATUS.SENT', status: 'default', icon: 'icon-send' },
	pending: { i18nKey: 'PROCESSES.DETAIL.MESSAGES.STATUS.PENDING', status: 'warning', icon: 'icon-steady-page' },
	failed: { i18nKey: 'PROCESSES.DETAIL.MESSAGES.STATUS.FAILED', status: 'error', icon: 'icon-alert-triangle' },
};

/**
 * Une SMS + WhatsApp del detalle de proceso.
 * Orden visual: más reciente primero (Figma). Ordinal: cronológico (1 = primer envío).
 */
export function mergeSentMessages(
	sms: IProcessSentMessage[] | null | undefined,
	whatsapp: IProcessSentMessage[] | null | undefined,
): IProcessSentMessageView[] {
	const smsItems = (Array.isArray(sms) ? sms : []).map((item) => withChannel(item, 'sms'));
	const whatsappItems = (Array.isArray(whatsapp) ? whatsapp : []).map((item) => withChannel(item, 'whatsapp'));

	const chronological = [...smsItems, ...whatsappItems].sort((a, b) => compareCreatedAt(a.created_at, b.created_at));

	const withOrder = chronological.map((item, index) => ({
		...item,
		order: index + 1,
	}));

	return withOrder.reverse();
}

export function resolveSentMessageReason(message: IProcessSentMessage): string {
	return (message.type || message.reason || '').trim();
}

/** Códigos de motivo API → clave i18n (p. ej. `2fa` → copy Figma). */
const REASON_TYPE_I18N: Record<string, string> = {
	'2fa': 'PROCESSES.DETAIL.MESSAGES.REASON_TYPES.TFA',
	tfa: 'PROCESSES.DETAIL.MESSAGES.REASON_TYPES.TFA',
	link: 'PROCESSES.DETAIL.MESSAGES.REASON_TYPES.LINK',
};

/** Etiqueta de motivo para UI (mapea códigos cortos como `2fa`). */
export function formatSentMessageReasonLabel(
	message: IProcessSentMessage,
	translateFn: (key: string) => string,
): string {
	const raw = resolveSentMessageReason(message);
	if (!raw) {
		return '-';
	}
	const i18nKey = REASON_TYPE_I18N[raw.toLowerCase()];
	return i18nKey ? translateFn(i18nKey) : raw;
}

export function resolveSentMessageContent(message: IProcessSentMessage): string {
	return (message.message || message.content || message.body || message.text || '').trim();
}

export function resolveSentMessageStatus(message: IProcessSentMessage): string {
	return (message.status || message.delivery_status || message.state || '').trim();
}

/** Etiqueta i18n de estado de entrega (mismo mapa que el modal de mensajes enviados). */
export function formatSentMessageStatusLabel(
	message: IProcessSentMessage,
	translateFn: (key: string) => string,
): string {
	const raw = resolveSentMessageStatus(message);
	if (!raw) {
		return '';
	}
	const mapped = STATUS_TAG_MAP[raw.trim().toLowerCase()];
	return mapped ? translateFn(mapped.i18nKey) : raw;
}

/**
 * Formatea a `+CC XXX XXX XXX` (bloques de 3).
 * - 9 dígitos → España (+34), habitual si el usuario no guardó prefijo.
 * - 11 dígitos empezando por 34 → ES; por 1 → USA.
 * @param emptyFallback Texto si no hay teléfono (por defecto `-`).
 */
export function formatPhoneDisplay(
	phone: string | null | undefined,
	emptyFallback: string = '-',
): string {
	if (!phone) {
		return emptyFallback;
	}
	const trimmed = phone.trim();
	if (!trimmed) {
		return emptyFallback;
	}

	const digits = trimmed.replace(/\D/g, '');
	if (digits.length < 8) {
		return trimmed;
	}

	let country: string;
	let national: string;

	if (digits.length === 9) {
		country = '34';
		national = digits;
	} else if (digits.length === 11 && digits.startsWith('34')) {
		country = '34';
		national = digits.slice(2);
	} else if (digits.length === 11 && digits.startsWith('1')) {
		country = '1';
		national = digits.slice(1);
	} else {
		const countryLen = Math.min(3, Math.max(1, digits.length - 9));
		country = digits.slice(0, countryLen);
		national = digits.slice(countryLen);
	}

	if (!national) {
		return `+${country}`;
	}

	const blocks: string[] = [];
	for (let i = 0; i < national.length; i += 3) {
		blocks.push(national.slice(i, i + 3));
	}
	return `+${country} ${blocks.join(' ')}`;
}

export function filterSentMessages(
	messages: IProcessSentMessageView[],
	filters: ISentMessagesFilterState,
	translateFn?: (key: string) => string,
): IProcessSentMessageView[] {
	const search = (filters.search || '').trim().toLowerCase();
	const startMs = toStartOfDayMs(filters.start_at);
	const endMs = toEndOfDayMs(filters.end_at);

	return messages.filter((message) => {
		if (search) {
			const phone = (message.phone || '').toLowerCase();
			const phoneDigits = phone.replace(/\D/g, '');
			const searchDigits = search.replace(/\D/g, '');
			const reasonRaw = resolveSentMessageReason(message).toLowerCase();
			const reasonLabel = (
				translateFn ? formatSentMessageReasonLabel(message, translateFn) : reasonRaw
			).toLowerCase();
			const matchesPhone =
				phone.includes(search) || (searchDigits.length > 0 && phoneDigits.includes(searchDigits));
			const matchesReason = reasonRaw.includes(search) || reasonLabel.includes(search);
			if (!matchesPhone && !matchesReason) {
				return false;
			}
		}

		const createdMs = toTimestampMs(message.created_at);
		if (startMs != null && (createdMs == null || createdMs < startMs)) {
			return false;
		}
		if (endMs != null && (createdMs == null || createdMs > endMs)) {
			return false;
		}

		return true;
	});
}

/**
 * `tec-comment` thread: teléfono (`tagImage.title`, sin avatar) + Motivo/Medio+ordinal + body.
 * Tag de estado se renderiza fuera (IComment thread no lo expone).
 */
export function mapSentMessageToRow(
	message: IProcessSentMessageView,
	translateFn: (key: string) => string,
): ISentMessageRowView {
	const content = resolveSentMessageContent(message);
	const hasContent = content.length > 0;
	const createdLabel = formatDate(message.created_at, 'dd/MM/yyyy - HH:mm');
	const createdMs = toTimestampMs(message.created_at) ?? 0;
	const mediumLabel = channelToLabel(message.channel, translateFn);
	const phoneLabel = formatPhoneDisplay(message.phone);
	const ordinalLabel = formatOrdinal(message.order);
	const statusTag = mapStatusTag(resolveSentMessageStatus(message), translateFn);

	const comment: IComment = {
		id: String(message.id ?? `${message.channel}-${message.order}-${message.created_at ?? ''}`),
		timestamp: createdMs,
		timestampLabel: createdLabel !== '-' ? createdLabel : undefined,
		infoGroup: {
			label: phoneLabel,
			tag: statusTag,
		},
		moreInfo: {
			from: {
				prefix: translateFn('PROCESSES.DETAIL.MESSAGES.REASON'),
				label: formatSentMessageReasonLabel(message, translateFn),
			},
			to: {
				prefix: translateFn('PROCESSES.DETAIL.MESSAGES.MEDIUM'),
				label: mediumLabel,
			},
			badge: {
				label: ordinalLabel,
				type: 'square',
			},
		},
		content: hasContent ? content : undefined,
	};

	return {
		id: comment.id,
		ordinalLabel,
		hasContent,
		comment,
	};
}

export function buildSentMessagesToolbar(
	translateFn: (key: string) => string,
	values?: ISentMessagesFilterState,
): IToolbar {
	return {
		search: {
			name: 'search',
			placeholder: translateFn('PROCESSES.DETAIL.MESSAGES.SEARCH_PLACEHOLDER'),
			value: values?.search || '',
		},
		filters: [
			{
				type: 'date',
				title: translateFn('FILTERS.FROM'),
				name: 'start_at',
				placeholder: translateFn('FILTERS.FROM'),
				labelChip: translateFn('FILTERS.FROM'),
				nameLimitMaxDate: 'end_at',
				rowWith: ['start_at', 'end_at'],
				value: values?.start_at ?? '',
				maxDate: endOfLocalToday(),
			},
			{
				type: 'date',
				title: translateFn('FILTERS.TO'),
				name: 'end_at',
				placeholder: translateFn('FILTERS.TO'),
				labelChip: translateFn('FILTERS.TO'),
				nameLimitMinDate: 'start_at',
				rowWith: ['start_at', 'end_at'],
				value: values?.end_at ?? '',
				maxDate: endOfLocalToday(),
			},
		],
	};
}

function withChannel(
	item: IProcessSentMessage,
	fallback: ProcessSentMessageChannel,
): IProcessSentMessage & {
	channel: ProcessSentMessageChannel;
} {
	const channel = normalizeChannel(item.channel) ?? fallback;
	return { ...item, channel };
}

function normalizeChannel(channel: string | undefined): ProcessSentMessageChannel | null {
	if (!channel) {
		return null;
	}
	const normalized = channel.toLowerCase();
	if (normalized === 'sms') {
		return 'sms';
	}
	if (normalized === 'whatsapp' || normalized === 'wa') {
		return 'whatsapp';
	}
	return null;
}

function compareCreatedAt(a?: string, b?: string): number {
	const aMs = toTimestampMs(a);
	const bMs = toTimestampMs(b);
	if (aMs == null && bMs == null) {
		return 0;
	}
	if (aMs == null) {
		return 1;
	}
	if (bMs == null) {
		return -1;
	}
	return aMs - bMs;
}

function toTimestampMs(value?: string | Date | null): number | null {
	if (value == null || value === '') {
		return null;
	}
	const date = value instanceof Date ? value : new Date(value);
	const ms = date.getTime();
	return Number.isNaN(ms) ? null : ms;
}

function toStartOfDayMs(value?: string | Date | null): number | null {
	const ms = toTimestampMs(value);
	if (ms == null) {
		return null;
	}
	const date = new Date(ms);
	date.setHours(0, 0, 0, 0);
	return date.getTime();
}

function toEndOfDayMs(value?: string | Date | null): number | null {
	const ms = toTimestampMs(value);
	if (ms == null) {
		return null;
	}
	const date = new Date(ms);
	date.setHours(23, 59, 59, 999);
	return date.getTime();
}

function formatOrdinal(order: number): string {
	return `${order}º`;
}

function channelToLabel(channel: ProcessSentMessageChannel, translateFn: (key: string) => string): string {
	if (channel === 'whatsapp') {
		return translateFn('OPERATIONS.WHATSAPP');
	}
	return translateFn('OPERATIONS.SMS');
}

function mapStatusTag(
	status: string | undefined,
	translateFn: (key: string) => string,
): { label: string; status: ITagStatus; icon: Icons } | undefined {
	if (!status) return undefined;

	const mapped = STATUS_TAG_MAP[status.trim().toLowerCase()];
	if (!mapped) return { label: status, status: 'default', icon: 'icon-info' };

	return { label: translateFn(mapped.i18nKey), status: mapped.status, icon: mapped.icon };
}
